import React, { memo } from "react";

/**
 * Props for the TableActionButtons component
 */
interface TableActionButtonsProps {
  /** Callback fired when Save button is clicked */
  onSave: () => void;
  /** Callback fired when Reset button is clicked */
  onReset: () => void;
  /** Text for the save button */
  saveText?: string;
  /** Text for the reset button */
  resetText?: string;
  /** Whether buttons are disabled */
  disabled?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * Reusable Save and Reset action buttons for table components
 * Provides consistent styling and behavior across all tables
 * 
 * @example
 * ```tsx
 * <TableActionButtons
 *   onSave={handleSave}
 *   onReset={handleReset}
 * />
 * ```
 */
const TableActionButtons: React.FC<TableActionButtonsProps> = memo(({
  onSave,
  onReset,
  saveText = "Save",
  resetText = "Reset",
  disabled = false,
  className = "",
}) => {
  return (
    <div className={`flex gap-4 pt-2! ${className}`}>
      <button
        onClick={onReset}
        disabled={disabled}
        className="button -light desktop:w-auto self-stretch flex items-center justify-center box-border max-w-none h-10"
        aria-label={resetText}
      >
        <span className="flex items-center justify-center w-full">
          {resetText}
        </span>
      </button>
      <button
        onClick={onSave}
        disabled={disabled}
        className="button -primary -light desktop:w-auto h-10 self-stretch flex items-center justify-center box-border max-w-none"
        aria-label={saveText}
      >
        <span className="flex items-center justify-center w-full">
          {saveText}
        </span>
      </button>
    </div>
  );
});

TableActionButtons.displayName = "TableActionButtons";

export default TableActionButtons;
